import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  DocumentFetchResult,
  DocumentMatchingFetchResult,
  DocumentSections,
  DocumentSingleFetchResult,
  DocumentSingleMatchingFetchResult,
  Entity,
  EntityTrackingConfig,
} from '../../models';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { EMPTY, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { DialogRef, DialogResult, DialogService } from '@progress/kendo-angular-dialog';
import { MercureOvertakenPopupMessageComponent } from '../../components/mercure/overtaken-popup-message/mercure-overtaken-popup-message.component';
import { MercureOvertakePopupActionComponent } from '../../components/mercure/overtake-popup-action/mercure-overtake-popup-action.component';
import { TranslateService } from '@ngx-translate/core';
import { CapitalizePipe } from '../../pipes';
import { ApiHttpService, ApplicationUserService, MercureService, TabService } from '../../services';
import { TrackingTypes } from '../../enums';
import { RefreshMap, SignalSlicePlan, SignalTopicBinding } from '../../types';

/** Snapshot of the visible slices for a signalKey, used to roll back a `beginEntityTransition`. */
interface EntityTransitionSnapshot {
  entity: Entity | undefined;
  items: Entity[] | undefined;
  totals: unknown;
  fees: Entity[] | undefined;
}

@Injectable({ providedIn: 'root' })
export class RouteEntitySubscriberService {
  #userService = inject(ApplicationUserService);
  #mercureService = inject(MercureService);
  #apiService = inject(ApiHttpService);
  #dialogService = inject(DialogService);
  #router = inject(Router);
  #translateService = inject(TranslateService);
  #tabService = inject(TabService);
  #capitalizePipe = new CapitalizePipe();
  #dialog: DialogRef | undefined;

  /** Channel used to broadcast line additions/deletions across tabs. */
  #broadcast = new BroadcastChannel('entity-items-change');
  #hasBootstrapped = false;

  #user = toSignal(this.#userService.user$, { initialValue: null });
  #configs = new Map<string, EntityTrackingConfig>();
  #signals = new Map<string, WritableSignal<Entity | undefined>>();
  #mercureSubs = new Map<string, Subscription>();
  #currentActiveSignalKey: WritableSignal<string | null> = signal(null);
  #itemsSignals = new Map<string, WritableSignal<Entity[] | undefined>>();
  #sectionsSignal = new Map<string, WritableSignal<Entity[] | undefined>>();
  #sectionItemsSignals = new Map<string, WritableSignal<DocumentSections<Entity> | undefined>>();
  #totalsSignals = new Map<string, WritableSignal<unknown>>();
  #sectionTotalsSignals = new Map<string, WritableSignal<unknown>>();
  #linkedItemsSignals = new Map<string, WritableSignal<DocumentSections<Entity> | undefined>>();
  #itemMappingsSignals = new Map<string, WritableSignal<DocumentSections<Entity> | undefined>>();
  #draftItemsSignals = new Map<string, WritableSignal<DocumentSections<Entity> | undefined>>();
  #versions = new Map<string, WritableSignal<number>>();
  isReadOnly = signal(false);
  #feesSignals = new Map<string, WritableSignal<Entity[] | undefined>>();

  /** Track the list of subscribed item topics per signal key */
  #itemTopicSubs = new Map<string, Map<string, Subscription>>();
  #subscriptions = new Map<string, Subscription>();
  #signalTopicBindings = new Map<string, { resolve: (topic: string) => SignalSlicePlan | null; refresh: RefreshMap }>();
  // mapping entityId with signalKey for stale-lock cleanup
  private readonly entityToSignalKey = new Map<string, string>();

  // thresholds (no numeric separators to avoid parser issues)
  private readonly staleThresholdMs = 30000; // 30s
  private readonly heartBeatMs = 9000; // 9s

  constructor() {
    this.#userService.user$
      .pipe(
        filter((u) => !!u),
        take(1),
      )
      .subscribe(() => this.start());
  }

  private bootstrap(): void {
    if (this.#hasBootstrapped) return;
    this.#hasBootstrapped = true;

    window.addEventListener('beforeunload', this.releaseAllLockedEntities);
    window.addEventListener('pagehide', this.releaseAllLockedEntities);

    // react when any heartbeat or lock key changes
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key?.startsWith('heartbeat-') || e.key?.startsWith('entity-open-')) {
        this.cleanupStaleLocks();
      }
    });

    this.startHeartbeat();

    // run cleanup every staleThresholdMs
    setInterval(() => this.cleanupStaleLocks(), this.staleThresholdMs);

    // navigation: subscribe or tear down detail locks
    this.#router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      const root = this.#router.routerState.snapshot.root;
      const match = this.findMatch(root);

      if (match) {
        // lock or re-lock the entity
        this.activateDetail(match);
      } else {
        // tear down last active lock
        this.deactivateLastDetail();
      }
    });

    // // ✅ Handle the *current* route once, for initial load/refresh
    // const currentRoot = this.#router.routerState.snapshot.root;
    // const currentMatch = this.findMatch(currentRoot);
    // if (currentMatch) {
    //   this.activateDetail(currentMatch);
    // } else {
    //   this.deactivateLastDetail();
    // }

    this.#broadcast.onmessage = (event) => {
      const msg = event.data;
      this.handleBroadcastMessage(msg);
    };
  }

  start(): void {
    this.bootstrap();

    const root = this.#router.routerState.snapshot.root;
    const match = this.findMatch(root);
    if (match) {
      this.activateDetail(match);
    } else {
      this.deactivateLastDetail();
    }
  }
  registerSignalTopicBinding(signalKey: string, binding: SignalTopicBinding) {
    this.#signalTopicBindings.set(signalKey, this.normalizeBinding(binding));
  }
  /**
   * Broadcast to all other tabs that an entity's items have changed.  The entityId
   * should be the '@id' (IRI) of the parent entity being edited.
   */
  broadcastItemsChange(signalKey: string, entityId: string): void {
    this.#broadcast.postMessage({ type: 'ITEMS_CHANGED', signalKey, entityId });
  }

  broadcastItemTopicChanged(signalKey: string, topicIri: string): void {
    this.#broadcast.postMessage({ type: 'TOPIC_CHANGED', signalKey, topic: topicIri });
  }

  /**
   * Called when a broadcast message is received (or from your own code) tohandleBroadcastMessage
   * refresh the items array and resubscribe to topics.  This only refetches
   * the items/totals and calls subscribeToItems() – it does not reload the
   * entire entity.
   */
  refreshItemsForSignal(signalKey: string, entityId: string): void {
    const config = this.#configs.get(signalKey);
    if (!config) return;

    // Determine what type of fetch we need (SINGLE vs MATCHING)
    config
      .fetch(entityId.replace(/^.*\/([^/]+)$/, '$1')) // extract ID if needed
      .pipe(take(1))
      .subscribe((docResult) => {
        // Update only items and totals; do not touch the main entity signal
        if (docResult.type === TrackingTypes.SINGLE) {
          this.getItemsSignal(signalKey).set(docResult.items as Entity[]);
          this.getTotalsSignal(signalKey).set(docResult['totals' as keyof typeof docResult] as Entity);
          this.getFeesSignal(signalKey).set(docResult.fees ?? []);
        } else if (docResult.type === TrackingTypes.MATCHING) {
          this.getSectionItemsSignal(signalKey).set(docResult.items as DocumentSections<Entity>);
          this.getSectionTotalsSignal(signalKey).set(
            docResult['totals' as keyof typeof docResult] as DocumentSections<Entity>,
          );
          this.getItemMappingsSignal(signalKey).set(
            docResult['itemMappings' as keyof typeof docResult] as DocumentSections<Entity>,
          );
          this.getDraftItemsSignal(signalKey).set(
            docResult['draftItems' as keyof typeof docResult] as DocumentSections<Entity>,
          );
          this.getLinkedItemsSignal(signalKey).set(
            docResult['linkedItems' as keyof typeof docResult] as DocumentSections<Entity>,
          );
        } else if (docResult.type === TrackingTypes.SINGLE_MATCHING) {
          this.getSectionItemsSignal(signalKey).set(docResult.items as DocumentSections<Entity>);
          this.getItemMappingsSignal(signalKey).set(
            docResult['itemMappings' as keyof typeof docResult] as DocumentSections<Entity>,
          );
          this.getDraftItemsSignal(signalKey).set(
            docResult['draftItems' as keyof typeof docResult] as DocumentSections<Entity>,
          );
          this.getLinkedItemsSignal(signalKey).set(
            docResult['linkedItems' as keyof typeof docResult] as DocumentSections<Entity>,
          );
        }

        // Flatten items and resubscribe to their SSE topics
        const items = this.extractAllItems(docResult);
        this.subscribeToItems(signalKey, items);
      });
  }

  private get tabId() {
    return this.#tabService.tabId;
  }

  // route-guard helper
  getConfigForRoute(
    snapshot: ActivatedRouteSnapshot,
  ): { config: EntityTrackingConfig; signalKey: string; entityId: string } | null {
    for (const [signalKey, config] of this.#configs.entries()) {
      const id = snapshot.paramMap.get(config.paramKey);
      if (id) return { config, signalKey, entityId: id };
    }
    return null;
  }

  private activateDetail(match: { config: EntityTrackingConfig; signalKey: string; entityId: string }) {
    const { config, signalKey, entityId } = match;

    const sig = this.#signals.get(signalKey)!;
    const existing = sig();
    const existingIri = existing?.['@id'];
    const existingId = existingIri?.split('/').pop();

    // ─────────────────────────────────────────────
    // CASE A: New or different entity
    // ─────────────────────────────────────────────
    //
    // This will only happen when:
    //  - there is no guard (classic behavior), OR
    //  - this is the very first time we ever see this entity
    //
    // For guarded routes, the guard already called initEntityContext
    // BEFORE NavigationEnd, so existing will be defined and IDs match.
    if (!existing || existingId !== entityId) {
      if (existingIri) {
        // clean up previous entity for this signalKey
        this.removeMercureSubscription(signalKey);
        this.unregisterTab(existingIri);
        this.unsubscribeFromItems(signalKey);
      }

      // clear old entity + slices
      sig.set(undefined);
      this.getItemsSignal(signalKey).set([]);
      this.getTotalsSignal(signalKey).set(undefined);
      this.getFeesSignal(signalKey).set(undefined);
      this.getLinkedItemsSignal(signalKey).set(undefined);
      this.getItemMappingsSignal(signalKey).set(undefined);
      this.getSectionItemsSignal(signalKey).set(undefined);
      this.getSectionTotalsSignal(signalKey).set(undefined);
      this.getDraftItemsSignal(signalKey).set(undefined);
      this.getSectionsSignals(signalKey).set(undefined);

      // A new document starts editable; the fetched lock below decides whether it becomes read-only.
      // (Without this, viewing a document locked by someone else left the next one read-only too.)
      this.isReadOnly.set(false);

      // Mark this signalKey as active immediately (synchronous) so that
      // deactivateLastDetail can always find it even if the user navigates
      // away before the async fetch below completes.
      this.setCurrentActiveSignalKey(signalKey);

      // Unguarded flow: fetch + (optionally) claim lock + initEntityContext
      const fetchSub = config
        .fetch(entityId)
        .pipe(
          switchMap((docResult) => {
            const entity = docResult.entity;
            const iri = entity['@id']!;
            const me = this.getCurrentUserUri();

            // If nobody owns it yet and we are not readonly → claim the lock
            if (me && !this.isReadOnly() && !entity.isUsedBy) {
              return config.updateUsage(iri, { isUsedBy: me }).pipe(
                tap(() => {
                  entity.isUsedBy = me;
                }),
                map(() => docResult),
              );
            }

            return of(docResult);
          }),
        )
        .subscribe((docResult) => {
          const entity = docResult.entity;
          const me = this.getCurrentUserUri();

          // If somebody else already owns it and we are NOT readonly, treat as kicked
          if (me && entity.isUsedBy && entity.isUsedBy !== me) {
            // this._doTakeover(entity['@id']!, entity, signalKey);
            this.isReadOnly.set(true);
            // this.initEntityContext(signalKey, entity, config, entityId, me ?? '', this.isReadOnly(), docResult);
            // return;
          }

          // Centralised seeding + subscriptions
          this.initEntityContext(signalKey, entity, config, entityId, me ?? '', this.isReadOnly(), docResult);
        });

      // Store so deactivateLastDetail can cancel it if the user navigates away
      // before the fetch + lock-claim completes.
      this.#subscriptions.get(signalKey)?.unsubscribe();
      this.#subscriptions.set(signalKey, fetchSub);

      return;
    }

    // ─────────────────────────────────────────────
    // CASE B: Same entity as already loaded
    // ─────────────────────────────────────────────
    //
    // This is the path hit when:
    //  - a guarded route has already called initEntityContext in the guard, and
    //    NavigationEnd fires afterwards, or
    //  - we just navigated back to a detail route for the same entity.
    const iri = existingIri!;
    this.setCurrentActiveSignalKey(signalKey);

    const me = this.getCurrentUserUri();

    // If we’re NOT readonly and nobody owns it, self-claim (self-heal)
    if (!this.isReadOnly() && existing && !existing.isUsedBy && me) {
      existing.isUsedBy = me;
      config.updateUsage(iri, { isUsedBy: me }).subscribe(() => {
        this.#mercureService.markAsLocallyUpdated(iri);
      });
    }

    // If another user owns it AND we are not readonly → treat as takeover
    if (!this.isReadOnly() && existing && existing.isUsedBy && existing.isUsedBy !== me) {
      this.handleTakeover(iri, existing);
      return;
    }

    // Ensure tab registration and (if needed) a live entity subscription.
    // For guarded routes, initEntityContext will already have subscribed,
    // so the isSubscribedTo() guard prevents duplicates.
    this.registerTabForEntity(iri, signalKey, config.releaseUrl(existing));
    // if (!this.#mercureService.isSubscribedTo(iri)) {
    this.subscribeToEntity(existing, signalKey);
    // }
  }

  private deactivateLastDetail() {
    const prevKey = this.#currentActiveSignalKey();
    if (!prevKey) return;

    const sig = this.#signals.get(prevKey)!;
    const entity = sig();

    if (entity?.['@id']) {
      const entityUri = entity['@id'];

      // Unsubscribe from the entity topic itself
      this.#mercureService.unsubscribeFromTopics([entityUri]);

      // Clean up item topic subscriptions and topics list
      // (destroyEntityContext will also call unsubscribeFromItems,
      // but calling it here again is harmless; or you can rely on destroyEntityContext only)
      // this.unsubscribeFromItems(prevKey);

      // localStorage + server lock cleanup
      const wasLast = this.unregisterTab(entityUri);
      const me = this.getCurrentUserUri();
      if (wasLast && entity.isUsedBy === me) {
        this.#configs.get(prevKey)!.updateUsage(entityUri, { isUsedBy: null }).subscribe();
      }
    }

    // Kill subscriptions + signals for this detail context
    this.destroyEntityContext(prevKey);

    // Reset active key
    this.#currentActiveSignalKey.set(null);
  }

  // subscribeToEntity<T extends Entity>(entity: T, signalKey: string) {
  //   const normId = (this.#mercureService as any).normalizeTopicId?.(entity?.['@id']) ?? entity?.['@id'];
  //   if (!normId) return;
  //
  //   this.removeMercureSubscription(signalKey);
  //
  //   const sub = this.#mercureService
  //     .getTopicUpdates(normId)
  //     .pipe(
  //       filter(
  //         (u): u is T => !!u && ((this.#mercureService as any).normalizeTopicId?.(u['@id']) ?? u['@id']) === normId,
  //       ),
  //     )
  //     .subscribe((update) => {
  //       const s = this.#signals.get(signalKey)! as WritableSignal<T | undefined>;
  //
  //       // keep previous state to detect "we just lost the lock"
  //       const previous = s();
  //       s.set({ ...(update as any) });
  //
  //       const me = this.getCurrentUserUri();
  //       const prevOwner = previous?.['isUsedBy'] as string | null | undefined;
  //       const newOwner = update?.['isUsedBy'] as string | null | undefined;
  //       const readonly = this.isReadOnly();
  //       // 🔍 debug logging – keep this while testing
  //       console.log('[LOCK] entity update', {
  //         normId,
  //         me,
  //         prevOwner,
  //         newOwner,
  //         readonly
  //       });
  //       const weJustLostLock =
  //         !!me &&
  //         !!newOwner &&
  //         newOwner !== me &&
  //         !readonly;
  //
  //       if (weJustLostLock) {
  //         this._doTakeover(normId, update, signalKey);
  //       }
  //     });
  //
  //   this.#mercureSubs.set(signalKey, sub);
  // }

  subscribeToEntity<T extends Entity>(entity: T, signalKey: string) {
    const normalizeTopic = (id?: string) => (id ? ((this.#mercureService as any).normalizeTopicId?.(id) ?? id) : id);

    const rawId = entity?.['@id'] as string | undefined;
    const normId = normalizeTopic(rawId);

    if (!normId) return;

    this.removeMercureSubscription(signalKey);

    const applyUpdate = (update: any) => {
      const updateRawId = update?.['@id'] as string | undefined;
      const updateNormId = normalizeTopic(updateRawId);

      // If the topic doesn't match after normalization, ignore it
      if (!updateNormId || updateNormId !== normId) {
        return;
      }

      const s = this.#signals.get(signalKey)! as WritableSignal<T | undefined>;

      const previous = s();
      s.set({ ...(update as any) });
      // the totals and fees have no Mercure topic of their own; reload them when the document changes
      // (e.g. turning transport fees off removes the generated fees)
      const binding = this.#signalTopicBindings.get(signalKey);
      binding?.refresh.totals?.();
      binding?.refresh.fees?.();

      const normalizeUser = (v?: string | null) => (v ? v.replace(/^https?:\/\/[^/]+/, '') : v);

      const me = normalizeUser(this.getCurrentUserUri() || undefined);
      const newOwner = normalizeUser(update?.['isUsedBy'] as string | null | undefined);
      const readOnly = this.isReadOnly();

      // For now, ignore readOnly in the detection to ensure we *see* takeovers
      const weJustLostLock = !!me && !!newOwner && newOwner !== me;

      if (weJustLostLock && !readOnly) {
        this._doTakeover(normId, update, signalKey);
      }
    };

    const sub = this.#mercureService.getTopicUpdates(normId).subscribe(applyUpdate);

    // Mercure does not replay what was published while this tab's stream was (re)connecting, such as another user
    // claiming the lock at the same moment (both tabs reloading at once let both claim it). Once the stream is
    // open, check the document and catch up if its lock or content changed in the meantime.
    const sameUser = (a?: string | null, b?: string | null) =>
      (a ? a.replace(/^https?:\/\/[^/]+/, '') : null) === (b ? b.replace(/^https?:\/\/[^/]+/, '') : null);
    sub.add(
      this.#mercureService.streamOpened$
        .pipe(
          switchMap(() =>
            this.#apiService.get(normId).pipe(
              map((res) => res.body as Entity),
              catchError(() => EMPTY),
            ),
          ),
        )
        .subscribe((fresh) => {
          const current = this.#signals.get(signalKey)?.();
          if (!current || normalizeTopic(current['@id'] as string | undefined) !== normId) return;
          if (sameUser(fresh.isUsedBy as string, current.isUsedBy as string) && fresh.updatedOn === current.updatedOn) return;
          applyUpdate(fresh);
        }),
    );

    this.#mercureSubs.set(signalKey, sub);
  }

  subscribeToItems(signalKey: string, items: Entity[]): void {
    // Collect next set of topics from the given items
    const nextTopics = new Set<string>();
    const newItems: Entity[] = [];

    items.forEach((item) => {
      if ((item as any).isFeeRow === true) return;
      const norm = (this.#mercureService as any).normalizeTopicId?.(item['@id']) ?? item['@id'];
      if (!norm) return;
      nextTopics.add(norm);
      newItems.push(item);
    });

    if (nextTopics.size === 0) {
      return;
    }

    // Ensure SSE connection is open for these NEW items
    this.#mercureService.subscribeToItemsIfPossible(newItems);

    // Get or create the per-topic subscription map for this signalKey
    let subsMap = this.#itemTopicSubs.get(signalKey);
    if (!subsMap) {
      subsMap = new Map<string, Subscription>();
      this.#itemTopicSubs.set(signalKey, subsMap);
    }

    // Unsubscribe from topics that are no longer in the new list
    const staleTopics = [...subsMap.entries()].filter(([topic]) => !nextTopics.has(topic));
    for (const [topic, sub] of staleTopics) {
      sub.unsubscribe();
      this.#mercureService.unsubscribeFromTopics([topic]);
      subsMap.delete(topic);
    }

    // The document itself is in the list to keep its topic in the stream, but subscribeToEntity already handles its
    // updates; listening here as well applied every document update twice (and reloaded the totals twice).
    const entityId = this.#signals.get(signalKey)?.()?.['@id'] as string | undefined;
    const entityTopic = this.#mercureSubs.has(signalKey) && entityId
      ? ((this.#mercureService as any).normalizeTopicId?.(entityId) ?? entityId)
      : null;

    // Subscribe to topics we don't yet have
    nextTopics.forEach((topic) => {
      if (subsMap!.has(topic) || topic === entityTopic) return;

      const sub = this.#mercureService
        .getTopicUpdates(topic)
        .subscribe((update: Entity) => this.applyItemUpdate(signalKey, update));

      subsMap!.set(topic, sub);
    });
  }

  /** Replace the corresponding item  and entity in all stored signals when an update arrives. */
  private applyItemUpdate(signalKey: string, update: Entity): void {
    if (!update) return;

    const rawId = update['@id'] as string | undefined;
    if (!rawId) return;

    const normalize = (id?: string) => (id ? ((this.#mercureService as any).normalizeTopicId?.(id) ?? id) : undefined);

    const topicIri = normalize(rawId);
    if (!topicIri) return;

    // 1) Update main entity signal (detail entity)
    const entitySig = this.#signals.get(signalKey);
    if (entitySig) {
      const currentEntity = entitySig();
      const currentId = normalize(currentEntity?.['@id'] as string | undefined);

      if (currentEntity && currentId === topicIri) {
        entitySig.set({ ...(update as any) });
      }
    }

    // 2) Update flat items list (TrackingTypes.SINGLE)
    const itemsSig = this.#itemsSignals.get(signalKey);
    if (itemsSig) {
      const list = itemsSig() ?? [];
      let changed = false;

      const next = list.map((it) => {
        const id = normalize(it['@id'] as string | undefined);
        if (id === topicIri) {
          changed = true;
          return update;
        }
        return it;
      });

      if (changed) {
        itemsSig.set(next);
      }
    }

    // 3) Update sections list
    const sectionsSig = this.#sectionsSignal.get(signalKey);
    if (sectionsSig) {
      const sections = sectionsSig() ?? [];
      let changed = false;

      const next = sections.map((it) => {
        const id = normalize(it['@id'] as string | undefined);
        if (id === topicIri) {
          changed = true;
          return update;
        }
        return it;
      });

      if (changed) {
        sectionsSig.set(next);
      }
    }

    // 4) Update all DocumentSections signals (items, mappings, linked, drafts)
    const sectionSigs = [
      this.#sectionItemsSignals.get(signalKey),
      this.#draftItemsSignals.get(signalKey),
      this.#itemMappingsSignals.get(signalKey),
      this.#linkedItemsSignals.get(signalKey),
    ];

    sectionSigs.forEach((sig) => {
      const sections = sig?.();
      if (!sections) return;

      let changed = false;
      const updatedSections: DocumentSections<Entity> = {};

      for (const [sectionKey, items] of Object.entries(sections)) {
        const nextItems = items.map((it) => {
          const id = normalize(it['@id'] as string | undefined);
          if (id === topicIri) {
            changed = true;
            return update;
          }
          return it;
        });

        updatedSections[sectionKey] = nextItems;
      }

      if (changed) {
        sig!.set(updatedSections);
      }
    });

    // 5) Use the SignalTopicBinding (refresh plan) to trigger targeted refreshes
    const binding = this.#signalTopicBindings.get(signalKey);
    if (!binding) return;

    const plan = binding.resolve(topicIri);
    if (plan) this.#runRefreshPlan(binding, plan);

    // A changed line changes the document totals and the fees derived from the lines (e.g. generated transport fees),
    // which have no Mercure topic of their own.
    // (Many bindings have no plan for item topics: the item itself was already replaced above.)
    if (plan?.slice !== 'totals') binding.refresh.totals?.();
    if (plan?.slice !== 'fees') binding.refresh.fees?.();
  }

  #runRefreshPlan(binding: { refresh: RefreshMap }, plan: SignalSlicePlan): void {
    switch (plan.slice) {
      case 'entity':
        return binding.refresh.entity?.();

      case 'totals':
        return binding.refresh.totals?.();

      case 'fees':
        return binding.refresh.fees?.();

      case 'sections':
        return binding.refresh.sections?.();

      case 'sectionTotals':
        return binding.refresh.sectionTotals?.(plan.sectionIri);

      case 'sectionItems':
        return binding.refresh.sectionItems?.(plan.sectionIri);

      case 'itemMappings':
        return binding.refresh.itemMappings?.(plan.sectionIri, plan.itemIri);

      case 'linkedItems':
        return binding.refresh.linkedItems?.(plan.sectionIri);

      case 'draftItems':
        return binding.refresh.draftItems?.(plan.sectionIri);

      case 'draftItemsBySection':
        return binding.refresh.draftItemsBySection?.(plan.sectionIri);

      case 'items': {
        // Prefer a dedicated items() refresher; fall back to itemMappings()
        if (binding.refresh.items) {
          return binding.refresh.items(plan.sectionIri, plan.itemIri);
        }
        return binding.refresh.itemMappings?.(plan.sectionIri, plan.itemIri);
      }
    }
  }
  /** Stop listening to item updates and forget the topics for this signalKey. */
  private unsubscribeFromItems(signalKey: string): void {
    const subsMap = this.#itemTopicSubs.get(signalKey);
    if (subsMap) {
      for (const [topic, sub] of subsMap.entries()) {
        sub.unsubscribe();
        this.#mercureService.unsubscribeFromTopics([topic]);
      }
      subsMap.clear();
      this.#itemTopicSubs.delete(signalKey);
    }
  }

  removeMercureSubscription(signalKey: string) {
    const sub = this.#mercureSubs.get(signalKey);
    if (sub) {
      sub.unsubscribe();
      this.#mercureSubs.delete(signalKey);
    }
  }

  // TAB-LOCK MANAGEMENT
  registerTabForEntity(entityUri: string, signalKey?: string, releaseUrl?: string) {
    const key = `entity-open-${entityUri}`;
    const entry = JSON.parse(localStorage.getItem(key) || '{}');
    entry[this.tabId] = true;
    localStorage.setItem(key, JSON.stringify(entry));

    if (signalKey) {
      this.entityToSignalKey.set(entityUri, signalKey);
    }

    // Store the release URL so any tab can fire a beacon even if it doesn't
    // have the entity loaded in memory (e.g. during stale-lock cleanup).
    if (releaseUrl) {
      localStorage.setItem(`entity-meta-${entityUri}`, JSON.stringify({ releaseUrl }));
    }
  }

  unregisterTab(entityUri: string): boolean {
    const key = `entity-open-${entityUri}`;
    const entry = JSON.parse(localStorage.getItem(key) || '{}');
    delete entry[this.tabId];
    if (Object.keys(entry).length === 0) {
      localStorage.removeItem(key);
      localStorage.removeItem(`entity-meta-${entityUri}`);
      return true;
    }
    localStorage.setItem(key, JSON.stringify(entry));
    return false;
  }

  getVersionSignal(key: string): WritableSignal<number> {
    let v = this.#versions.get(key);
    if (!v) {
      v = signal(0);
      this.#versions.set(key, v);
    }
    return v;
  }

  #bumpVersion(key: string) {
    const v = this.getVersionSignal(key);
    v.update((n) => n + 1);
  }

  releaseBySignalKeyIfLastTab(key: string, unlock: (iri: string) => Observable<unknown>): Observable<boolean> {
    const sig = this.#signals.get(key);
    const ent = sig?.();
    const iri = ent?.['@id'];
    if (!iri) return of(true);

    const wasLast = this.unregisterTab(iri);
    this.removeMercureSubscription(key);
    if (!wasLast) return of(true);

    const me = this.getCurrentUserUri();
    if (ent?.isUsedBy && me && ent.isUsedBy !== me) return of(true);

    return unlock(iri).pipe(
      tap(() => {
        sig!.set(undefined);
      }),
      map(() => true),
      catchError(() => of(true)),
    );
  }

  // STALE-LOCK CLEANUP
  // STALE-LOCK CLEANUP
  private cleanupStaleLocks() {
    const now = Date.now();
    // allow some drift / throttling
    const maxAge = this.staleThresholdMs + 5000;

    const myTabId = this.tabId;
    const myHbTS = Number(localStorage.getItem(`heartbeat-${myTabId}`));
    const myHbOk = !!myHbTS && now - myHbTS <= maxAge;

    // Snapshot all keys upfront — removing keys during an index-based loop
    // shifts indices and causes entries to be silently skipped.
    const allKeys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)!);

    // A) remove dead heartbeats
    for (const k of allKeys) {
      if (!k.startsWith('heartbeat-')) continue;

      const ts = Number(localStorage.getItem(k));
      if (!ts || now - ts > maxAge) {
        localStorage.removeItem(k);
      }
    }

    // B) per-entity open-tab entries
    for (const key of allKeys) {
      if (!key.startsWith('entity-open-')) continue;

      let entry: Record<string, boolean> | null = null;
      try {
        entry = JSON.parse(localStorage.getItem(key) || '{}');
      } catch {
        // corrupt entry → remove and move on
        localStorage.removeItem(key);
        continue;
      }
      if (!entry) continue;

      const entityId = key.replace('entity-open-', '');

      // ❌ REMOVED: no more "self-heal" for this tab here.
      // We only prune stale tab IDs and unlock if *nobody* is left.

      let changed = false;
      for (const tid of Object.keys(entry)) {
        const hb = Number(localStorage.getItem(`heartbeat-${tid}`));
        if (!hb || now - hb > maxAge) {
          delete entry[tid];
          changed = true;
        }
      }

      if (!changed) continue;

      if (Object.keys(entry).length === 0) {
        // last viewer really gone → unlock server + clear in-memory
        localStorage.removeItem(key);
        const match = this.findConfigByEntityId(entityId);
        if (match) {
          // Guard: if this entity is still actively loaded in this tab, self-heal instead of
          // unlocking. This prevents the skeleton flash caused by tab suspension making the
          // heartbeat appear stale while the user is still actively viewing the document.
          const isCurrentlyActive = match.signalKey === this.getCurrentActiveSignalKey();
          const entityStillLoaded = !!this.#signals.get(match.signalKey)?.();
          if (isCurrentlyActive && entityStillLoaded) {
            this.registerTabForEntity(entityId, match.signalKey);
            localStorage.setItem(`heartbeat-${this.tabId}`, Date.now().toString());
            continue;
          }
          localStorage.removeItem(`entity-meta-${entityId}`);
          match.config
            .updateUsage(entityId, { isUsedBy: null })
            .pipe(take(1))
            .subscribe(() => this.clearInMemoryLock(match.signalKey));
        } else {
          // This tab doesn't have the entity loaded — use the stored release URL
          // to fire a best-effort beacon without needing the config in memory.
          const meta = this.#readEntityMeta(entityId);
          localStorage.removeItem(`entity-meta-${entityId}`);
          if (meta?.releaseUrl) {
            const blob = new Blob([JSON.stringify({ isUsedBy: null })], { type: 'application/ld+json' });
            navigator.sendBeacon(meta.releaseUrl, blob);
          }
        }
      } else {
        // persist pruned entry
        localStorage.setItem(key, JSON.stringify(entry));
      }
    }

    // C) in-memory locks with no localStorage entry
    const me = this.getCurrentUserUri();

    for (const [signalKey, sig] of this.#signals.entries()) {
      const ent = sig();
      if (!ent || ent.isUsedBy !== me) continue;

      const lsKey = `entity-open-${ent['@id']}`;
      if (localStorage.getItem(lsKey)) continue;

      if (myHbOk) {
        // Self-heal ONLY if *this* service instance already holds the entity
        // but LS entry went missing. This cannot happen in a brand-new tab,
        // because there are no signals there yet.
        const mappedKey =
          this.entityToSignalKey.get(ent['@id'] as string) ?? this.getCurrentActiveSignalKey() ?? undefined;

        this.registerTabForEntity(ent['@id'] as string, mappedKey);
        continue;
      }

      // Truly stale: my heartbeat not OK, so unlock on server and clear local
      const cfg = this.#configs.get(signalKey)!;
      cfg
        .updateUsage(ent['@id'] as string, { isUsedBy: null })
        .pipe(take(1))
        .subscribe(() => this.clearInMemoryLock(signalKey));
    }
  }

  private clearInMemoryLock(signalKey: string) {
    const sig = this.#signals.get(signalKey)!;
    const ent = sig();
    sig.set(undefined);
    this.#currentActiveSignalKey.set(null);

    if (ent?.['@id']) {
      this.unregisterTab(ent['@id']);
      this.entityToSignalKey.delete(ent['@id']);
      this.#mercureService.unsubscribeFromTopics([ent['@id']]);
    }
  }

  #readEntityMeta(entityUri: string): { releaseUrl: string } | null {
    try {
      return JSON.parse(localStorage.getItem(`entity-meta-${entityUri}`) || 'null');
    } catch {
      return null;
    }
  }

  private findConfigByEntityId(entityId: string): { config: EntityTrackingConfig; signalKey: string } | null {
    const sk = this.entityToSignalKey.get(entityId);
    if (!sk) return null;
    return { config: this.#configs.get(sk)!, signalKey: sk };
  }

  // helper function for service signals
  trackEntity<
    T extends Entity,
    V extends Entity,
    X extends Entity,
    U extends Entity,
    W extends Entity,
    S extends Entity,
  >(config: EntityTrackingConfig) {
    this.#configs.set(config.signalKey, config);

    // Only create the signals once. trackEntity runs more than once at startup (setup constructor +
    // trackEntities() after /cookie); replacing the signal objects would orphan the instances that
    // guards already seeded and components already captured, leaving them stuck on undefined.
    const key = config.signalKey;
    const ensure = <M extends Map<string, unknown>>(target: M, create: () => unknown) => {
      if (!target.has(key)) target.set(key, create());
    };

    ensure(this.#signals, () => signal<T | undefined>(undefined));
    ensure(this.#itemsSignals, () => signal<V[]>([]));
    ensure(this.#linkedItemsSignals, () => signal<DocumentSections<X> | undefined>(undefined));
    ensure(this.#sectionItemsSignals, () => signal<DocumentSections<V> | undefined>(undefined));
    ensure(this.#sectionTotalsSignals, () => signal<DocumentSections<any> | undefined>(undefined));
    ensure(this.#itemMappingsSignals, () => signal<DocumentSections<U> | undefined>(undefined));
    ensure(this.#draftItemsSignals, () => signal<DocumentSections<W> | undefined>(undefined));
    ensure(this.#totalsSignals, () => signal<unknown>(undefined));
    ensure(this.#feesSignals, () => signal<U[] | undefined>(undefined));
    ensure(this.#sectionsSignal, () => signal<S[]>([]));
  }

  setSignal<T extends Entity>(signalKey: string, value: T) {
    const s = this.#signals.get(signalKey);
    if (!s) {
      this.#signals.set(signalKey, signal<T | undefined>(value));
    } else {
      const fresh = value == null ? value : ({ ...(value as any) } as T);
      s.set(fresh);
    }

    this.#bumpVersion(signalKey);
  }

  getSignal<T extends Entity>(signalKey: string): WritableSignal<T | undefined> {
    return this.#signals.get(signalKey)! as WritableSignal<T | undefined>;
  }

  setCurrentActiveSignalKey(key: string) {
    this.#currentActiveSignalKey.set(key);
  }

  getCurrentActiveSignalKey(): string | null {
    return this.#currentActiveSignalKey();
  }

  unSetSignal(signalKey: string) {
    // Clear in place: swapping the signal object would orphan components still holding the old one.
    const s = this.#signals.get(signalKey);
    if (s) s.set(undefined);
    else this.#signals.set(signalKey, signal(undefined));
  }

  getItemsSignal<T extends Entity>(key: string): WritableSignal<T[] | undefined> {
    const s = this.#itemsSignals.get(key);
    if (!s) throw new Error(`No items-signal for ${key}`);
    return s as WritableSignal<T[] | undefined>;
  }

  getTotalsSignal<T>(key: string): WritableSignal<T> {
    const s = this.#totalsSignals.get(key) as WritableSignal<T | undefined> | undefined;
    if (!s) throw new Error(`No totals-signal for ${key}`);
    return s as WritableSignal<T>;
  }

  getFeesSignal<T>(key: string): WritableSignal<T[] | undefined> {
    const f = this.#feesSignals.get(key);
    if (!f) throw new Error(`No fees-signal for ${key}`);
    return f as WritableSignal<T[] | undefined>;
  }

  getLinkedItemsSignal<T extends Entity>(key: string): WritableSignal<DocumentSections<T> | undefined> {
    const s = this.#linkedItemsSignals.get(key);
    if (!s) throw new Error(`No clientOrderItems-signal for ${key}`);
    return s as WritableSignal<DocumentSections<T> | undefined>;
  }

  getSectionTotalsSignal<T extends Entity>(key: string): WritableSignal<DocumentSections<T> | undefined> {
    const s = this.#sectionTotalsSignals.get(key);
    if (!s) throw new Error(`No section-totals-signal for ${key}`);
    return s as WritableSignal<DocumentSections<T> | undefined>;
  }

  getItemMappingsSignal<T extends Entity>(key: string): WritableSignal<DocumentSections<T> | undefined> {
    const s = this.#itemMappingsSignals.get(key);
    if (!s) throw new Error(`No itemMappings-signal for ${key}`);
    return s as WritableSignal<DocumentSections<T> | undefined>;
  }

  getSectionItemsSignal<T extends Entity>(key: string): WritableSignal<DocumentSections<T> | undefined> {
    const s = this.#sectionItemsSignals.get(key);
    if (!s) throw new Error(`No section-items-signal for ${key}`);
    return s as WritableSignal<DocumentSections<T> | undefined>;
  }

  getDraftItemsSignal<T extends Entity>(key: string): WritableSignal<DocumentSections<T> | undefined> {
    const s = this.#draftItemsSignals.get(key);
    if (!s) throw new Error(`No DraftItems-signal for ${key}`);
    return s as WritableSignal<DocumentSections<T> | undefined>;
  }

  getSectionsSignals<T>(key: string): WritableSignal<T[] | undefined> {
    const s = this.#sectionsSignal.get(key);
    if (!s) throw new Error(`No sections-signal for ${key}`);
    return s as WritableSignal<T[] | undefined>;
  }

  getItemsSignalValues<T extends Entity>(key: string): Observable<T[]> {
    return of(this.getItemsSignal(key)() as T[]);
  }

  getTotalsSignalValues<T>(key: string): Observable<T> {
    return of(this.getTotalsSignal(key)() as T);
  }

  // heartbeat + beacon
  releaseActiveIfOwned(): Observable<void> {
    const sk = this.#currentActiveSignalKey();
    if (!sk) return of(void 0);

    const sig = this.#signals.get(sk)!;
    const ent = sig();
    const cfg = this.#configs.get(sk)!;
    const me = this.getCurrentUserUri();
    const released = ent && ent.isUsedBy === me && this.unregisterTab(ent['@id']!);

    if (!ent || !released) return of(void 0);

    ent.isUsedBy = null;
    return cfg.updateUsage(ent['@id']!, { isUsedBy: null }).pipe(
      tap(() => sig.set(undefined)),
      tap(() => this.#currentActiveSignalKey.set(null)),
      tap(() => this.unSetSignal(sk)),
      tap(() => this.#mercureService.unsubscribeFromTopics([ent['@id']!])),
      map(() => void 0),
    );
  }

  private releaseAllLockedEntities = () => {
    localStorage.removeItem(`heartbeat-${this.tabId}`);

    for (const [sk, s] of this.#signals.entries()) {
      const e = s();
      const cfg = this.#configs.get(sk);
      const me = this.getCurrentUserUri();
      if (e?.['@id'] && cfg && e.isUsedBy === me && !this.isReadOnly()) {
        // Only release the lock on the server when this is the last tab
        // viewing the entity. Other open tabs should keep the lock alive.
        if (!this.#isLastTabWithEntity(e['@id'])) continue;

        const blob = new Blob([JSON.stringify({ isUsedBy: null })], {
          type: 'application/ld+json',
        });
        navigator.sendBeacon(cfg.releaseUrl(e), blob);
      }
    }
  };

  #isLastTabWithEntity(entityUri: string): boolean {
    try {
      const entry = JSON.parse(localStorage.getItem(`entity-open-${entityUri}`) || '{}');
      const tabIds = Object.keys(entry);
      return tabIds.length === 0 || (tabIds.length === 1 && tabIds[0] === this.tabId);
    } catch {
      return true;
    }
  }

  confirmTakeover(entity: Entity, config: EntityTrackingConfig): Observable<DialogResult> {
    const title = 'OVERTAKE_ENTITY';
    this.#dialog = this.#dialogService.open({
      content: MercureOvertakePopupActionComponent,
      title: this.#capitalizePipe.transform(
        this.#translateService.instant(title, {
          entity: this.#translateService.instant(config.translationsKey ?? 'DOCUMENT'),
        }),
      ),
      cssClass: 'k-px-16',
    });
    this.#dialog.content.instance.user.set(entity['isUsedByFullName']);
    this.#dialog.content.instance.details.set(entity['reference' as keyof Entity]);
    return this.#dialog.result;
  }

  private _doTakeover(entityUri: string, update: Entity, signalKey: string) {
    const me = this.getCurrentUserUri();
    if (update.isUsedBy === me) return;

    this.#signals.get(signalKey)!.set(undefined);
    this.unregisterTab(entityUri);
    this.entityToSignalKey.delete(entityUri);
    this.#mercureService.unsubscribeFromTopics([entityUri]);
    this.unsubscribeFromItems(signalKey);

    this.#router.navigate([this.#configs.get(signalKey)!.listRoute]).then(() => {
      this.#dialog = this.#dialogService.open({
        content: MercureOvertakenPopupMessageComponent,
        title: this.#capitalizePipe.transform(
          this.#translateService.instant('ENTITY_TAKEN_OVER', {
            entity: this.#translateService.instant(this.#configs.get(signalKey)!.translationsKey!),
          }),
        ),
        cssClass: 'k-px-16',
      });
      const cmp = this.#dialog.content.instance;
      cmp.user?.set(update.isUsedByFullName);
      cmp.details?.set(update['reference' as keyof Entity] as string);

      this.#dialog.result.subscribe(() => {
        this.#currentActiveSignalKey.set(null);
      });
    });
  }

  private handleTakeover(entityUri: string, update: Entity) {
    const signalKey = this.#currentActiveSignalKey();
    const me = this.getCurrentUserUri();
    if (!signalKey) return;
    // Ignore if the update is from ourselves
    if (update.isUsedBy === me || this.isReadOnly()) return;

    // Clear the signal and local locks for this entity
    this.#signals.get(signalKey)!.set(undefined);
    this.unregisterTab(entityUri);
    this.entityToSignalKey.delete(entityUri);

    // Unsubscribe from the entity’s Mercure topic
    this.#mercureService.unsubscribeFromTopics([entityUri]);

    // NEW: Unsubscribe from any item topics associated with this detail
    this.unsubscribeFromItems(signalKey);

    // Navigate back to the list view and show takeover popup
    const config = this.#configs.get(signalKey)!;
    this.#router.navigate([config.listRoute]).then(() => {
      this.#dialog = this.#dialogService.open({
        content: MercureOvertakenPopupMessageComponent,
        title: this.#capitalizePipe.transform(
          this.#translateService.instant('ENTITY_TAKEN_OVER', {
            entity: this.#translateService.instant(config.translationsKey!),
          }),
        ),
        cssClass: 'k-px-16',
      });
      const cmp = this.#dialog.content.instance;
      cmp.user?.set(update.isUsedByFullName);
      cmp.details?.set(update['reference' as keyof Entity] as string);

      this.#dialog.result.subscribe(() => {
        // Reset active key after the dialog is closed
        this.#currentActiveSignalKey.set(null);
      });
    });
  }

  private startHeartbeat() {
    const tabId = this.tabId;

    try {
      localStorage.setItem(`heartbeat-${tabId}`, Date.now().toString());
    } catch (err) {
      console.warn('initial heartbeat write failed', err);
    }

    setInterval(() => {
      try {
        localStorage.setItem(`heartbeat-${tabId}`, Date.now().toString());
      } catch (err) {
        console.warn('heartbeat write failed', err);
      }
    }, this.heartBeatMs);
  }

  private findMatch(
    snapshot: ActivatedRouteSnapshot,
  ): { config: EntityTrackingConfig; signalKey: string; entityId: string } | null {
    for (const [signalKey, cfg] of this.#configs.entries()) {
      const id = snapshot.paramMap.get(cfg.paramKey);
      if (id) return { config: cfg, signalKey, entityId: id };
    }
    for (const child of snapshot.children) {
      const m = this.findMatch(child);
      if (m) return m;
    }
    return null;
  }

  getCurrentUserUri(): string | null {
    const u = this.#user();
    return u ? `/users/${u.id}` : null;
  }

  private extractAllItems(docResult: {
    type: TrackingTypes;
    entity?: Entity;
    items?: Entity[] | DocumentSections<Entity>;
    draftItems?: DocumentSections<Entity>;
    linkedItems?: DocumentSections<Entity>;
    itemMappings?: DocumentSections<Entity>;
    sections?: Entity[];
  }): Entity[] {
    const allItems: Entity[] = [];
    if (docResult.entity) {
      allItems.push(docResult.entity);
    }
    // Top-level items
    if (docResult.type === TrackingTypes.SINGLE) {
      if (Array.isArray(docResult.items)) {
        allItems.push(...(docResult.items as Entity[]));
      }
    } else if (docResult.type === TrackingTypes.MATCHING) {
      const items = docResult.items as DocumentSections<Entity>;
      const sections = docResult.sections;
      Object.values(items).forEach((sec) => allItems.push(...sec));
      sections?.forEach((sec) => allItems.push(sec));
    }

    // Additional section properties we want to flatten
    const sectionKeys: Array<keyof typeof docResult> = ['draftItems', 'linkedItems', 'itemMappings'];

    sectionKeys.forEach((key) => {
      const sec = docResult[key] as DocumentSections<Entity> | undefined;
      if (sec) {
        Object.values(sec).forEach((subSec) => allItems.push(...subSec));
      }
    });

    return allItems;
  }

  /** Locally unsubscribe from a single item's Mercure topic for a given signalKey. */
  private unsubscribeItemTopic(signalKey: string, topic: string): void {
    const subsMap = this.#itemTopicSubs.get(signalKey);
    if (!subsMap) return;

    const sub = subsMap.get(topic);
    if (sub) {
      sub.unsubscribe();
      subsMap.delete(topic);
    }

    this.#mercureService.unsubscribeFromTopics([topic]);
    this.removeItemFromSignals(signalKey, topic);
  }

  requestUnsubscribeItemAcrossTabs(signalKey: string, itemIri: string): void {
    const norm = (this.#mercureService as any).normalizeTopicId?.(itemIri) ?? itemIri;

    // Local unsubscribe
    this.unsubscribeItemTopic(signalKey, norm);

    // Broadcast to other tabs
    this.#broadcast.postMessage({
      type: 'UNSUBSCRIBE_ITEM',
      signalKey,
      topic: norm,
    });
  }

  /** Remove an item from all item signals for a given signalKey by its Mercure topic id. */
  private removeItemFromSignals(signalKey: string, topic: string): void {
    const normalize = (id: string | undefined) =>
      id ? ((this.#mercureService as any).normalizeTopicId?.(id) ?? id) : undefined;

    const normTopic = topic;

    // 1) Flat items list (TrackingTypes.SINGLE)
    const itemsSig = this.#itemsSignals.get(signalKey);
    if (itemsSig) {
      const list = itemsSig() ?? [];
      const filtered = list.filter((it) => normalize(it['@id'] as string) !== normTopic);
      if (filtered.length !== list.length) {
        itemsSig.set(filtered);
      }
    }

    // 2) Sectioned signals (TrackingTypes.MATCHING)
    const sectionSigs = [
      this.#sectionItemsSignals.get(signalKey),
      this.#draftItemsSignals.get(signalKey),
      this.#itemMappingsSignals.get(signalKey),
      this.#linkedItemsSignals.get(signalKey),
    ];

    sectionSigs.forEach((sig) => {
      const sections = sig?.();
      if (!sections) return;

      let changed = false;
      const updated: DocumentSections<Entity> = {};

      for (const [sectionKey, items] of Object.entries(sections)) {
        const filtered = items.filter((it) => normalize(it['@id'] as string) !== normTopic);
        if (filtered.length !== items.length) {
          changed = true;
        }
        updated[sectionKey] = filtered;
      }

      if (changed) {
        sig!.set(updated);
      }
    });
  }

  private handleBroadcastMessage(msg: any): void {
    switch (msg?.type) {
      case 'ITEMS_CHANGED': {
        const signalKey: string | undefined = msg.signalKey;
        const entityIri: string | undefined = msg.entityId;
        if (!signalKey || !entityIri) return;

        const currentEntity = this.getSignal(signalKey)?.();
        if (!currentEntity || currentEntity['@id'] !== entityIri) return;
        // Legacy/fallback: full items+totals refresh (avoid using this for granular changes)
        this.refreshItemsForSignal(signalKey, entityIri);
        return;
      }

      case 'TOPIC_CHANGED': {
        const signalKey: string | undefined = msg.signalKey;
        const topic: string | undefined = msg.topic;

        if (!signalKey || !topic) return;

        const binding = this.#signalTopicBindings.get(signalKey);
        if (binding) {
          const plan = binding.resolve(topic);
          if (plan) {
            switch (plan.slice) {
              case 'entity':
                return binding.refresh.entity?.();
              case 'totals':
                return binding.refresh.totals?.();
              case 'fees':
                return binding.refresh.fees?.();
              case 'sections':
                return binding.refresh.sections?.();
              case 'sectionTotals':
                return binding.refresh.sectionTotals?.(plan.sectionIri);
              case 'sectionItems':
                return binding.refresh.sectionItems?.(plan.sectionIri);
              case 'itemMappings':
                return binding.refresh.itemMappings?.(plan.sectionIri, plan.itemIri);
              case 'linkedItems':
                return binding.refresh.linkedItems?.(plan.sectionIri);
              case 'draftItems':
                return binding.refresh.draftItems?.(plan.sectionIri);
              case 'items': {
                if (binding.refresh.items) {
                  return binding.refresh.items(plan.sectionIri, plan.itemIri);
                }
                return binding.refresh.itemMappings?.(plan.sectionIri, plan.itemIri);
              }
              case 'draftItemsBySection': {
                return binding.refresh.draftItemsBySection?.(plan.sectionIri);
              }
            }
          }
        }

        // Fallback: refresh items/totals for current entity if binding didn’t match.
        const currentEntity = this.getSignal(signalKey)?.();
        if (currentEntity?.['@id']) this.refreshItemsForSignal(signalKey, currentEntity['@id'] as string);
        return;
      }

      case 'UNSUBSCRIBE_ITEM': {
        if (!msg.signalKey || !msg.topic) return;
        this.unsubscribeItemTopic(msg.signalKey, msg.topic);
        return;
      }
    }
  }

  initEntityContext<T extends Entity>(
    signalKey: string,
    initialEntity: T,
    config: EntityTrackingConfig,
    entityId: string,
    currentUserUri: string,
    readOnly: boolean,
    docResult?:
      | DocumentSingleFetchResult<Entity, Entity, Entity, Entity>
      | DocumentMatchingFetchResult<Entity, Entity, Entity, Entity, Entity, Entity>
      | DocumentSingleMatchingFetchResult<Entity, Entity, Entity, Entity, Entity>,
    options: { skipBackgroundFetch?: boolean } = {},
  ): void {
    // --- 1. clear slices ---
    this.setAllSignalsToUndefined(signalKey);
    this.isReadOnly.set(readOnly);

    // --- 2. seed entity and active key ---
    this.setSignal(signalKey, initialEntity);
    this.setCurrentActiveSignalKey(signalKey);

    if (initialEntity['@id']) {
      this.registerTabForEntity(initialEntity['@id']!, signalKey, config.releaseUrl(initialEntity));
    }

    const processResult = (
      res:
        | DocumentSingleFetchResult<Entity, Entity, Entity, Entity>
        | DocumentMatchingFetchResult<Entity, Entity, Entity, Entity, Entity, Entity>
        | DocumentSingleMatchingFetchResult<Entity, Entity, Entity, Entity, Entity>,
    ) => {
      if (!res) return;

      // freshest entity
      this.setSignal(signalKey, res.entity as T);

      if (res.type === TrackingTypes.SINGLE) {
        const single = res as DocumentSingleFetchResult<Entity, Entity, Entity, Entity>;
        this.getItemsSignal(signalKey).set([...(single.items ?? [])]);
        this.getTotalsSignal(signalKey).set(single.totals);
        this.getFeesSignal(signalKey).set(single.fees);
      }
      if (res.type === TrackingTypes.SINGLE_MATCHING) {
        const single = res as DocumentSingleMatchingFetchResult<Entity, Entity, Entity, Entity, Entity>;
        this.getSectionItemsSignal(signalKey).set(single.items);
        this.getItemMappingsSignal(signalKey).set(single.itemMappings);
        this.getDraftItemsSignal(signalKey).set(single.draftItems);
        this.getLinkedItemsSignal(signalKey).set(single.linkedItems);
      }

      if (res.type === TrackingTypes.MATCHING) {
        const m = res as DocumentMatchingFetchResult<Entity, Entity, Entity, Entity, Entity, Entity>;
        this.getSectionItemsSignal(signalKey).set(m.items);
        this.getItemMappingsSignal(signalKey).set(m.itemMappings);
        this.getDraftItemsSignal(signalKey).set(m.draftItems);
        this.getLinkedItemsSignal(signalKey).set(m.linkedItems);
        this.getSectionsSignals(signalKey).set(m.sections);
      }

      // subscribe to entity itself (live lock / updates)
      const iri = res.entity['@id'] as string | undefined;
      if (iri) {
        this.subscribeToEntity(res.entity as Entity, signalKey);
      }

      // subscribe to all items (centralized)
      const items = this.flattenItems(res);
      this.subscribeToItems(signalKey, items);

      // keep local isUsedBy in sync with current user
      // res.entity.isUsedBy = currentUserUri;
    };

    // --- 3. use provided result OR fetch ourselves ---
    if (docResult) {
      // No HTTP call here, just process
      processResult(docResult);
      this.#subscriptions.get(signalKey)?.unsubscribe();
      this.#subscriptions.set(signalKey, new Subscription()); // dummy, so cleanup is safe
      return;
    }

    if (options.skipBackgroundFetch) {
      return;
    }

    const sub = config
      .fetch(entityId, initialEntity)
      .pipe(
        tap((res) => processResult(res)),
        catchError((err) => {
          console.error('Background lock fetch failed', err);
          return of(null);
        }),
      )
      .subscribe();

    this.#subscriptions.get(signalKey)?.unsubscribe();
    this.#subscriptions.set(signalKey, sub);
  }

  /** Flatten a fetch result into a flat array of all items (SINGLE or MATCHING). */
  flattenItems(docResult: {
    type: TrackingTypes;
    entity: Entity;
    items?: unknown; // array or DocumentSections<Entity>
    draftItems?: Record<string, Entity[]>;
    linkedItems?: Record<string, Entity[]>;
    itemMappings?: Record<string, Entity[]>;
    sections?: Entity[];
  }): Entity[] {
    const all: Entity[] = [];

    if (docResult.type === TrackingTypes.SINGLE) {
      const itemsArray = docResult.items as Entity[] | undefined;
      if (Array.isArray(itemsArray)) {
        all.push(...itemsArray);
      }
    } else if (docResult.type === TrackingTypes.MATCHING) {
      const items = docResult.items as Record<string, Entity[]> | undefined;
      if (items) {
        Object.values(items).forEach((sec) => all.push(...sec));
      }

      const sections = docResult.sections as Entity[] | undefined;
      if (sections) {
        all.push(...sections);
      }

      const extraSections = [docResult.draftItems, docResult.linkedItems, docResult.itemMappings];
      extraSections.forEach((sec) => {
        if (sec) {
          Object.values(sec).forEach((sub) => all.push(...sub));
        }
      });
    } else if (docResult.type === TrackingTypes.SINGLE_MATCHING) {
      const items = docResult.items as Record<string, Entity[]> | undefined;
      if (items) {
        Object.values(items).forEach((sec) => all.push(...sec));
      }
      const mappingsArray = docResult.itemMappings as Record<string, Entity[]> | undefined;
      if (mappingsArray) {
        Object.values(mappingsArray).forEach((sec) => all.push(...sec));
      }
      if (Array.isArray(items)) {
        all.push(...items);
      }
      if (Array.isArray(mappingsArray)) {
        all.push(...mappingsArray);
      }
    }

    if (docResult.entity) {
      all.push(docResult.entity);
    }

    return all;
  }

  /**
   * Called by route guards as soon as navigation to a different entity begins,
   * so the document wrapper's skeleton renders immediately instead of the
   * previous document's data staying on screen while the guard's async
   * fetch/lock-claim chain resolves. No-op if the target id is already loaded.
   * Returns a snapshot to pass to `abortEntityTransition` if that chain fails.
   */
  beginEntityTransition(signalKey: string, entityId: string): EntityTransitionSnapshot | null {
    const sig = this.#signals.get(signalKey);
    if (!sig) return null;

    const existing = sig();
    const existingIri = existing?.['@id'];
    const existingId = existingIri?.split('/').pop();
    if (existingId === entityId) return null;

    const snapshot: EntityTransitionSnapshot = {
      entity: existing,
      items: this.getItemsSignal(signalKey)(),
      totals: this.getTotalsSignal(signalKey)(),
      fees: this.getFeesSignal<Entity>(signalKey)(),
    };

    if (existingIri) {
      // Tear down the previous entity's live subscriptions now, not just its signal
      // value: otherwise a mercure update for it (e.g. the lock-release echo fired by
      // canDeactivate while this navigation's guard is still fetching the new entity)
      // can land on the still-open old subscription and overwrite the signal we're
      // about to repopulate for the new entity, flashing stale data back on screen.
      this.removeMercureSubscription(signalKey);
      this.unregisterTab(existingIri);
      this.unsubscribeFromItems(signalKey);
    }

    sig.set(undefined);
    this.setAllSignalsToUndefined(signalKey);
    this.getFeesSignal(signalKey).set(undefined);

    return snapshot;
  }

  /** Rolls back a transition started by `beginEntityTransition` when the guard's fetch/lock chain fails. */
  abortEntityTransition(signalKey: string, snapshot: EntityTransitionSnapshot | null): void {
    if (!snapshot) return;

    this.#signals.get(signalKey)?.set(snapshot.entity);
    this.getItemsSignal(signalKey).set(snapshot.items);
    this.getTotalsSignal(signalKey).set(snapshot.totals);
    this.getFeesSignal(signalKey).set(snapshot.fees);
  }

  /** Optional: call when leaving the detail route to clean up. */
  destroyEntityContext(signalKey: string): void {
    this.#subscriptions.get(signalKey)?.unsubscribe();
    this.#subscriptions.delete(signalKey);

    this.setAllSignalsToUndefined(signalKey);
    // Optionally clear the entity as well
    // 4) Clear the entity signal itself
    const sig = this.#signals.get(signalKey);
    if (sig) {
      sig.set(undefined);
    }
  }

  setAllSignalsToUndefined(signalKey: string) {
    this.getItemsSignal(signalKey).set(undefined);
    this.getTotalsSignal(signalKey).set(undefined);
    this.getItemMappingsSignal(signalKey).set(undefined);
    this.getDraftItemsSignal(signalKey).set(undefined);
    this.getLinkedItemsSignal(signalKey).set(undefined);
    this.getSectionsSignals(signalKey).set(undefined);
    this.getSectionItemsSignal(signalKey).set(undefined);
    this.getSectionTotalsSignal(signalKey).set(undefined);
  }

  normalizeBinding(binding: SignalTopicBinding): {
    resolve: (topicIri: string) => SignalSlicePlan | null;
    refresh: RefreshMap;
  } {
    if ('resolve' in binding) return binding; // classic mode still supported

    const { patterns = {}, resolvers = {}, refresh } = binding;

    const resolve = (topicIri: string): SignalSlicePlan | null => {
      // 1) direct, pattern-only slices (no resolvers needed)
      if (patterns.entity?.test(topicIri)) return { slice: 'entity' };
      if (patterns.totals?.test(topicIri)) return { slice: 'totals' };
      if (patterns.fees?.test(topicIri)) return { slice: 'fees' };
      if (patterns.sections?.test(topicIri)) return { slice: 'sections' };

      // if you decide to support a dedicated "sectionTotals" topic
      if (patterns.sectionTotals?.test(topicIri)) {
        return { slice: 'sectionTotals', sectionIri: topicIri };
      }

      if (patterns.sectionItems?.test(topicIri)) {
        return { slice: 'sectionItems', sectionIri: topicIri };
      }

      // 2) slices that require mapping back to a section via resolvers
      if (patterns.items?.test(topicIri)) {
        const sectionIri = resolvers.sectionByItem?.(topicIri);
        if (sectionIri) return { slice: 'items', sectionIri, itemIri: topicIri };
        return null; // no guesswork, no defaults
      }

      if (patterns.itemMappings?.test(topicIri)) {
        const sectionIri = resolvers.sectionByMapping?.(topicIri);
        if (sectionIri) return { slice: 'itemMappings', sectionIri };
        return null;
      }

      if (patterns.linkedItems?.test(topicIri)) {
        const sectionIri = resolvers.sectionByLinked?.(topicIri);
        if (sectionIri) return { slice: 'linkedItems', sectionIri };
        return null;
      }

      if (patterns.draftItems?.test(topicIri)) {
        const sectionIri = resolvers.sectionByDraft?.(topicIri);
        if (sectionIri) return { slice: 'draftItems', sectionIri };
        return null;
      }

      if (patterns.draftItemsBySection?.test(topicIri)) {
        const sectionIri = resolvers.draftItemsBySection?.(topicIri) ?? topicIri.replace(/\/draft_items$/, '');
        if (sectionIri) return { slice: 'draftItemsBySection', sectionIri };
        return null;
      }

      return null; // unknown topic for this binding
    };

    return { resolve, refresh };
  }
}
